import { IChecklistCategory, IChecklistItem, IChecklistItemRow, IChecklistPopulated } from "@mctypes/checklist.types";
import { IMinUserDepricated } from "@mctypes/global.types";
import { ICheckTypes, IRowMoveInfo } from "@mcapps/shared/schemas/checklistSchema";
import { normalizeId } from "@mcapps/shared/sharedUtils";
import { IIssueApi } from "@schemas/issueSchema";
import { GridApi, SideBarDef } from "ag-grid-community";

export const sameId = (left: unknown, right: unknown) => normalizeId(left) === normalizeId(right);

export const getChecklistItemCountByCategoryId = (items: IChecklistItem[]): Record<string, number> =>
  items.reduce<Record<string, number>>((countByCategoryId, item) => {
    const categoryId = normalizeId(item.category);
    if (categoryId.length === 0) {
      return countByCategoryId;
    }

    countByCategoryId[categoryId] = Math.max(countByCategoryId[categoryId] ?? 0, item.itemId);
    return countByCategoryId;
  }, {});

export const getResponsibleColumnWidth = (
  categories: IChecklistCategory[],
  checkKey: string,
  users: { value: string; label: string }[],
): number => {
  const usersById = new Map(users.map((user) => [user.value, user.label]));
  const longestNameLength = categories.reduce((longest, category) => {
    const assignedTo = category.checks?.[checkKey]?.assignedTo;
    const name = typeof assignedTo === "string" ? usersById.get(assignedTo) : assignedTo?.name;
    return Math.max(longest, (name ?? "Ingen ansvarlig").length);
  }, 0);

  return Math.min(360, Math.max(180, longestNameLength * 8 + 88));
};

export const getLatestChecklistChangeLabel = (
  items: IChecklistItem[],
  createdAt?: string | Date | null,
): string | null => {
  const creationTime = createdAt == null ? NaN : new Date(createdAt).getTime();
  const latestChange = items.reduce(
    (latest, item) => {
      const candidates = [
        {
          updatedDate: item.inactive?.updatedDate,
          updatedBy: item.inactive?.updatedBy,
        },
        ...Object.values(item.checks ?? {}),
      ];

      candidates.forEach((candidate) => {
        if (candidate?.updatedDate == null) {
          return;
        }

        const candidateDate = new Date(candidate.updatedDate);
        // Template stamps and initialization timestamps are not changes to this checklist.
        if (Number.isNaN(candidateDate.getTime()) || candidateDate.getTime() <= creationTime) {
          return;
        }

        if (latest == null || candidateDate > latest.updatedDate) {
          latest = {
            updatedDate: candidateDate,
            updatedBy: candidate.updatedBy,
          };
        }
      });

      return latest;
    },
    null as { updatedDate: Date; updatedBy?: string | IMinUserDepricated } | null,
  );

  if (latestChange == null) {
    return null;
  }

  return latestChange.updatedDate.toLocaleString("no-NO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getChecklistExportColumnKeys = (columnIds: string[]): string[] =>
  columnIds.filter((columnId) => columnId !== "expand" && columnId !== "edit-actions");

export const CHECKLIST_TABLE_SIDE_BAR: SideBarDef = {
  toolPanels: [
    {
      id: "columns",
      labelDefault: "Columns",
      labelKey: "columns",
      iconKey: "columns",
      toolPanel: "agColumnsToolPanel",
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivotMode: true,
      },
    },
  ],
  position: "left",
};

export const getChecklistTableRowsRefactored = (
  checklist: IChecklistPopulated,
  checklistCategories: IChecklistCategory[],
  checklistItems: IChecklistItem[],
): IChecklistItemRow[] => {
  if (checklist == null || checklistCategories == null) {
    return [];
  }

  return checklistCategories.flatMap((cat) => {
    const categoryItems = checklistItems.filter((item) => sameId(item.category, cat._id));
    const categoryHasItems = categoryItems.length > 0;
    let categoryInactive = categoryHasItems;

    let itemRows: IChecklistItemRow[] = categoryItems.map((item) => {
      if (item.inactive?.isChecked === false) {
        categoryInactive = false;
      }

      return {
        _id: item._id,
        originalCategory: cat,
        inactive: item.inactive,
        title: item.title,
        reference: item.reference ?? "",
        collapsed: cat.collapsed === true,
        originalItem: item,
        comment: item.comment,
        commentImages: (item.commentImages ?? []).slice(0, 1),
        checks: {
          check1: item.checks?.check1,
          check2: item.checks?.check2,
          check3: item.checks?.check3,
          check4: item.checks?.check4,
          check5: item.checks?.check5,
          check6: item.checks?.check6,
          check7: item.checks?.check7,
          check8: item.checks?.check8,
          check9: item.checks?.check9,
          check10: item.checks?.check10,
          check11: item.checks?.check11,
          check12: item.checks?.check12,
          check13: item.checks?.check13,
          check14: item.checks?.check14,
          check15: item.checks?.check15,
        },
        otherAttributesData: item.otherAttributesData,
        itemId: item.itemId,
        categoryId: cat.categoryId,
        rowId: `${cat.categoryId}.${item.itemId}`,
        connectedIssues: item.connectedIssues as IIssueApi[],
        comments: item.comments,
        itemTags: item.itemTags ?? [],
      };
    });

    if (checklist.itemTagsActive === true && (checklist.activeItemTags?.length ?? 0) > 0 && itemRows.length > 0) {
      itemRows = itemRows.filter((row) => checklist.activeItemTags.every((tag) => row.itemTags.includes(tag)));
    }

    if (
      checklist.itemTagsActive === true &&
      (checklist.activeItemTags?.length ?? 0) > 0 &&
      categoryHasItems === true &&
      itemRows.length === 0
    ) {
      return [];
    }

    const categoryRow: IChecklistItemRow = {
      _id: cat._id,
      originalCategory: cat,
      dueDate: cat.dueDate,
      inactive: {
        isChecked: categoryInactive === true && categoryHasItems === true,
        updatedDate: null,
        updatedBy: null,
      },
      title: cat.title,
      reference: "",
      collapsed: cat.collapsed === true,
      originalItem: null,
      comment: null,
      commentImages: [],
      checks: null,
      itemId: null,
      categoryId: cat.categoryId,
      rowId: cat.categoryId.toString(),
    };

    return [categoryRow, ...itemRows];
  });
};

export const getChecklistDragTargetCategoryIndex = (api: GridApi<IChecklistItemRow>, newPosition: number): number => {
  let foundIndex = -1;
  const displayRow = api.getDisplayedRowAtIndex(newPosition - 1);
  const prevRowCategoryId = displayRow?.data?.categoryId ?? -1;

  api.forEachNode((node) => {
    const data = node.data;

    if (data?.originalItem == null && data?.categoryId === prevRowCategoryId) {
      foundIndex = node.rowIndex ?? -1;
    }
  });

  return foundIndex;
};

export const getChecklistRowMoveInfo = ({
  draggedRow,
  newPosition,
  newCategoryIndex,
  newCategoryRow,
  checklistId,
}: {
  draggedRow: IChecklistItemRow;
  newPosition: number;
  newCategoryIndex: number;
  newCategoryRow?: IChecklistItemRow;
  checklistId: string;
}): IRowMoveInfo => {
  const isCat = draggedRow.originalItem == null;
  const newRelativePosition = newPosition - newCategoryIndex;

  return {
    isCat,
    oldCategoryId: draggedRow.originalCategory._id,
    newCategoryId: newCategoryRow?.originalCategory?._id ?? null,
    rowId: isCat ? draggedRow.originalCategory._id : draggedRow.originalItem._id,
    position: isCat ? newPosition : newRelativePosition - 1,
    checklistId,
  };
};

export const getActiveChecklistCheckKeys = (checklist?: IChecklistPopulated | null): string[] => {
  return (Object.entries(checklist?.checkTypes ?? {}) as Array<[keyof ICheckTypes, ICheckTypes[keyof ICheckTypes]]>)
    .filter(([, checkType]) => checkType?.active === true)
    .map(([key]) => key);
};
